import { IconButton } from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { isUniqueFieldName } from '@databases/utils/field-name';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  type RefObject,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

const containerStyles = xcss({
  paddingBlockStart: 'space.150',
  paddingInline: 'space.150',
  paddingBlockEnd: 'space.0'
});

const errorStyles = xcss({
  width: '224px'
});

const errorContentStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  columnGap: 'space.050'
});

const errorTextStyles = xcss({
  overflowWrap: 'anywhere'
});

const infoIconWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  paddingInlineEnd: 'space.100'
});

const infoIconStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px'
});

const DESCRIPTION_MAX_LENGTH = 2000;
const FIELD_MENU_TEXT_BOX_STYLE = {
  width: '224px',
  height: '32px',
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

export type FieldMenuHeaderHandle = {
  save: () => boolean;
};

type FieldMenuHeaderProps = {
  name: string;
  description?: string;
  existingFieldNames: string[];
  onSaveName: (name: string) => void;
  onSaveDescription: (description: string) => void;
  onRequestClose: () => void;
  nameInputRef?: RefObject<HTMLInputElement | null>;
};

const FieldMenuHeader = forwardRef<FieldMenuHeaderHandle, FieldMenuHeaderProps>(
  function FieldMenuHeader(
    {
      name: originalName,
      description: originalDescription = '',
      existingFieldNames,
      onSaveName,
      onSaveDescription,
      onRequestClose,
      nameInputRef
    },
    ref
  ) {
    const { formatMessage } = useIntl();
    const [name, setName] = useState(originalName);
    const [description, setDescription] = useState(originalDescription);
    const savedDescriptionRef = useRef(originalDescription);
    const [isDescriptionVisible, setIsDescriptionVisible] = useState(
      originalDescription.length > 0
    );
    const [autoFocusDescription, setAutoFocusDescription] = useState(false);
    const isNameUnique = useMemo(
      () => isUniqueFieldName(name, existingFieldNames),
      [existingFieldNames, name]
    );

    const saveDescription = useCallback(() => {
      if (description !== savedDescriptionRef.current) {
        onSaveDescription(description);
        savedDescriptionRef.current = description;
      }
    }, [description, onSaveDescription]);

    const save = useCallback(() => {
      if (!isNameUnique) {
        return false;
      }

      const nextName = name.trim() || originalName;
      setName(nextName);
      if (nextName !== originalName) {
        onSaveName(nextName);
      }
      saveDescription();
      return true;
    }, [isNameUnique, name, onSaveName, originalName, saveDescription]);

    useImperativeHandle(ref, () => ({ save }), [save]);

    const cancel = useCallback(() => {
      setName(originalName);
      setDescription(originalDescription);
      onRequestClose();
    }, [onRequestClose, originalDescription, originalName]);

    const handleNameKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          if (save()) {
            event.currentTarget.blur();
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }
      },
      [cancel, save]
    );

    const handleDescriptionKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
          if (save()) {
            event.currentTarget.blur();
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }
      },
      [cancel, save]
    );

    return (
      <Box xcss={containerStyles} testId="field-menu-header">
        <Stack space="space.075">
          <Textfield
            ref={nameInputRef}
            autoFocus
            autoComplete="off"
            testId="field-menu-name-input"
            value={name}
            isInvalid={!isNameUnique}
            style={FIELD_MENU_TEXT_BOX_STYLE}
            aria-label={formatMessage(messages['field-settings.field-name'])}
            onChange={(event) => setName(event.currentTarget.value)}
            onKeyDown={handleNameKeyDown}
            elemAfterInput={
              <Box xcss={infoIconStyles}>
                {!isDescriptionVisible && (
                  <Tooltip
                    content={formatMessage(messages['field-settings.field-description'])}
                  >
                    <Box xcss={infoIconWrapperStyles}>
                      <IconButton
                        appearance="subtle"
                        spacing="compact"
                        testId="field-menu-description-button"
                        label={formatMessage(
                          messages['field-settings.field-description']
                        )}
                        icon={(iconProps) => (
                          <StatusInformationIcon
                            {...iconProps}
                            size="medium"
                            color={token('color.icon.subtle')}
                          />
                        )}
                        onClick={() => {
                          setIsDescriptionVisible(true);
                          setAutoFocusDescription(true);
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            }
          />
          {!isNameUnique && (
            <Box xcss={errorStyles}>
              <Box xcss={errorContentStyles}>
                <StatusErrorIcon
                  label=""
                  color={token('color.icon.danger')}
                  size="small"
                />
                <Box xcss={errorTextStyles}>
                  <Text color="color.text.danger" size="small">
                    {formatMessage(messages['field-context-menu.duplicate-field-name'], {
                      fieldName: name
                    })}
                  </Text>
                </Box>
              </Box>
            </Box>
          )}
          {isDescriptionVisible && (
            <Textfield
              autoFocus={autoFocusDescription}
              testId="field-menu-description-input"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              style={FIELD_MENU_TEXT_BOX_STYLE}
              aria-label={formatMessage(
                messages['field-settings.field-description.label']
              )}
              placeholder={formatMessage(
                messages['field-context-menu.description-placeholder']
              )}
              onChange={(event) => setDescription(event.currentTarget.value)}
              onBlur={saveDescription}
              onKeyDown={handleDescriptionKeyDown}
            />
          )}
        </Stack>
      </Box>
    );
  }
);

export default FieldMenuHeader;
